import { getContractStatusLabel } from 'components/ContractStatus';
import type { UerContractData, ContractResultPlanData, Maybe, ContractStatusEnum } from 'gql/graphql';
import { formatDateToLocale, isNotNil, newDate, toISOString } from 'utils';

import * as R from 'ramda';

import { costRoundingItems } from '../../Common/utils';

const statusPrepare = (statusId: ContractStatusEnum) => ({
    id: statusId,
    label: getContractStatusLabel(statusId),
});

const costRoundingPrepare = (costRounding?: number) => costRoundingItems.find((i) => i.id === costRounding);

export interface ParsedContractData
    extends Omit<UerContractData, 'signingDate' | 'startDate' | 'finishDate' | 'status' | 'costRounding'> {
    signingDate: Date;
    startDate: Date;
    finishDate: Date;
    status: { id: ContractStatusEnum; label: string };
    cost: {
        contractPlan: string;
        contractPlanTax: string;
        contractPlanWithTax: string;
    };
    costRounding: ReturnType<typeof costRoundingPrepare>;
}

export const parseContractData: (contract: UerContractData) => ParsedContractData = R.pipe(
    R.pickBy<ParsedContractData>(isNotNil),
    R.evolve({
        signingDate: newDate,
        startDate: newDate,
        finishDate: newDate,
        status: statusPrepare,
        costRounding: costRoundingPrepare,
    }),
) as any;

export const prepareContractData = R.pipe(
    R.pickBy(isNotNil),
    R.evolve({
        id: Number,
        projectId: Number,
        status: R.prop('id'),
        internalNumber: String,
        amountWithoutTax: Number,
        amountWithTax: Number,
        tax: Number,
        uerOthersPercent: Number,
        signingDate: toISOString,
        startDate: toISOString,
        finishDate: toISOString,
        name: R.defaultTo('name'),
        projectName: R.defaultTo('projectName'),
        costRounding: R.prop('id'),
    }),
);

export const getLabelContract = (contract?: ParsedContractData) =>
    `Договор${contract?.internalNumber ? ` №${contract?.internalNumber}` : ''}${
        contract?.signingDate ? ` от ${formatDateToLocale(contract.signingDate)}` : ''
    }`;

const contractPlanDataInputMap: Record<string, string> = {
    'Глава 1-7': 'chapter1_7',
    'Глава 8': 'chapter8',
    'Глава 9': 'chapter9',
    ОП: 'equipmentContractor',
    ОЗ: 'equipmentCustomer',
    Инфляция: 'inflation',
    МП: 'materialContractor',
    МЗ: 'materialCustomer',
};

export const contractPlanFieldNameGetter = (row: any): string => {
    const name = contractPlanDataInputMap[row?.name as string];

    return name ? `contractPlan.${name}` : '';
};

export const contractPlansContractPlanMapper = (planItems?: Maybe<Array<ContractResultPlanData>>) =>
    planItems?.reduce((acc, item) => {
        const { contractPlan, name } = item;
        const fieldName = contractPlanDataInputMap[name as string];

        if (fieldName) {
            const lens = R.lensPath(['contractPlan', fieldName]);

            acc = R.set(lens, contractPlan, acc);
        }

        return acc;
    }, {});
